/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `time_in_zone` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class TimeInZoneMesg : Mesg {
    public constructor() : super("TimeInZone", Profile.MesgNum.TIME_IN_ZONE)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** reference_mesg. */
    public var referenceMesg: MesgNum?
        get() = (getFieldValue(REFERENCE_MESG_FIELD_NUM) as? UShort)?.let { MesgNum.fromValue(it) }
        set(value) = setFieldValue(REFERENCE_MESG_FIELD_NUM, value?.value)

    /** reference_index. */
    public var referenceIndex: UShort?
        get() = getFieldValue(REFERENCE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(REFERENCE_INDEX_FIELD_NUM, value)

    /** Number of values stored in time_in_hr_zone. */
    public val numTimeInHrZone: Int
        get() = getField(TIME_IN_HR_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_hr_zone, in s. */
    public var timeInHrZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_HR_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInHrZone(it) }
        }
        set(value) {
            removeField(TIME_IN_HR_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInHrZone(index, element) }
        }

    public fun getTimeInHrZone(index: Int): Double? =
        getFieldValue(TIME_IN_HR_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInHrZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_HR_ZONE_FIELD_NUM, value, index)
    }

    /** Number of values stored in time_in_speed_zone. */
    public val numTimeInSpeedZone: Int
        get() = getField(TIME_IN_SPEED_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_speed_zone, in s. */
    public var timeInSpeedZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_SPEED_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInSpeedZone(it) }
        }
        set(value) {
            removeField(TIME_IN_SPEED_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInSpeedZone(index, element) }
        }

    public fun getTimeInSpeedZone(index: Int): Double? =
        getFieldValue(TIME_IN_SPEED_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInSpeedZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_SPEED_ZONE_FIELD_NUM, value, index)
    }

    /** Number of values stored in time_in_cadence_zone. */
    public val numTimeInCadenceZone: Int
        get() = getField(TIME_IN_CADENCE_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_cadence_zone, in s. */
    public var timeInCadenceZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_CADENCE_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInCadenceZone(it) }
        }
        set(value) {
            removeField(TIME_IN_CADENCE_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInCadenceZone(index, element) }
        }

    public fun getTimeInCadenceZone(index: Int): Double? =
        getFieldValue(TIME_IN_CADENCE_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInCadenceZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_CADENCE_ZONE_FIELD_NUM, value, index)
    }

    /** Number of values stored in time_in_power_zone. */
    public val numTimeInPowerZone: Int
        get() = getField(TIME_IN_POWER_ZONE_FIELD_NUM)?.numValues ?: 0

    /** time_in_power_zone, in s. */
    public var timeInPowerZone: List<Double?>?
        get() {
            val field = getField(TIME_IN_POWER_ZONE_FIELD_NUM) ?: return null
            return List(field.numValues) { getTimeInPowerZone(it) }
        }
        set(value) {
            removeField(TIME_IN_POWER_ZONE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setTimeInPowerZone(index, element) }
        }

    public fun getTimeInPowerZone(index: Int): Double? =
        getFieldValue(TIME_IN_POWER_ZONE_FIELD_NUM, index) as? Double

    public fun setTimeInPowerZone(index: Int, value: Double?) {
        setFieldValue(TIME_IN_POWER_ZONE_FIELD_NUM, value, index)
    }

    /** Number of values stored in hr_zone_high_boundary. */
    public val numHrZoneHighBoundary: Int
        get() = getField(HR_ZONE_HIGH_BOUNDARY_FIELD_NUM)?.numValues ?: 0

    /** hr_zone_high_boundary, in bpm. */
    public var hrZoneHighBoundary: List<UByte?>?
        get() {
            val field = getField(HR_ZONE_HIGH_BOUNDARY_FIELD_NUM) ?: return null
            return List(field.numValues) { getHrZoneHighBoundary(it) }
        }
        set(value) {
            removeField(HR_ZONE_HIGH_BOUNDARY_FIELD_NUM)
            value?.forEachIndexed { index, element -> setHrZoneHighBoundary(index, element) }
        }

    public fun getHrZoneHighBoundary(index: Int): UByte? =
        getFieldValue(HR_ZONE_HIGH_BOUNDARY_FIELD_NUM, index) as? UByte

    public fun setHrZoneHighBoundary(index: Int, value: UByte?) {
        setFieldValue(HR_ZONE_HIGH_BOUNDARY_FIELD_NUM, value, index)
    }

    /** Number of values stored in speed_zone_high_boundary. */
    public val numSpeedZoneHighBoundary: Int
        get() = getField(SPEED_ZONE_HIGH_BOUNDARY_FIELD_NUM)?.numValues ?: 0

    /** speed_zone_high_boundary, in m/s. */
    public var speedZoneHighBoundary: List<Double?>?
        get() {
            val field = getField(SPEED_ZONE_HIGH_BOUNDARY_FIELD_NUM) ?: return null
            return List(field.numValues) { getSpeedZoneHighBoundary(it) }
        }
        set(value) {
            removeField(SPEED_ZONE_HIGH_BOUNDARY_FIELD_NUM)
            value?.forEachIndexed { index, element -> setSpeedZoneHighBoundary(index, element) }
        }

    public fun getSpeedZoneHighBoundary(index: Int): Double? =
        getFieldValue(SPEED_ZONE_HIGH_BOUNDARY_FIELD_NUM, index) as? Double

    public fun setSpeedZoneHighBoundary(index: Int, value: Double?) {
        setFieldValue(SPEED_ZONE_HIGH_BOUNDARY_FIELD_NUM, value, index)
    }

    /** Number of values stored in cadence_zone_high_bondary. */
    public val numCadenceZoneHighBondary: Int
        get() = getField(CADENCE_ZONE_HIGH_BONDARY_FIELD_NUM)?.numValues ?: 0

    /** cadence_zone_high_bondary, in rpm. */
    public var cadenceZoneHighBondary: List<UByte?>?
        get() {
            val field = getField(CADENCE_ZONE_HIGH_BONDARY_FIELD_NUM) ?: return null
            return List(field.numValues) { getCadenceZoneHighBondary(it) }
        }
        set(value) {
            removeField(CADENCE_ZONE_HIGH_BONDARY_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCadenceZoneHighBondary(index, element) }
        }

    public fun getCadenceZoneHighBondary(index: Int): UByte? =
        getFieldValue(CADENCE_ZONE_HIGH_BONDARY_FIELD_NUM, index) as? UByte

    public fun setCadenceZoneHighBondary(index: Int, value: UByte?) {
        setFieldValue(CADENCE_ZONE_HIGH_BONDARY_FIELD_NUM, value, index)
    }

    /** Number of values stored in power_zone_high_boundary. */
    public val numPowerZoneHighBoundary: Int
        get() = getField(POWER_ZONE_HIGH_BOUNDARY_FIELD_NUM)?.numValues ?: 0

    /** power_zone_high_boundary, in watts. */
    public var powerZoneHighBoundary: List<UShort?>?
        get() {
            val field = getField(POWER_ZONE_HIGH_BOUNDARY_FIELD_NUM) ?: return null
            return List(field.numValues) { getPowerZoneHighBoundary(it) }
        }
        set(value) {
            removeField(POWER_ZONE_HIGH_BOUNDARY_FIELD_NUM)
            value?.forEachIndexed { index, element -> setPowerZoneHighBoundary(index, element) }
        }

    public fun getPowerZoneHighBoundary(index: Int): UShort? =
        getFieldValue(POWER_ZONE_HIGH_BOUNDARY_FIELD_NUM, index) as? UShort

    public fun setPowerZoneHighBoundary(index: Int, value: UShort?) {
        setFieldValue(POWER_ZONE_HIGH_BOUNDARY_FIELD_NUM, value, index)
    }

    /** hr_calc_type. */
    public var hrCalcType: HrZoneCalc?
        get() = (getFieldValue(HR_CALC_TYPE_FIELD_NUM) as? UByte)?.let { HrZoneCalc.fromValue(it) }
        set(value) = setFieldValue(HR_CALC_TYPE_FIELD_NUM, value?.value)

    /** max_heart_rate. */
    public var maxHeartRate: UByte?
        get() = getFieldValue(MAX_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(MAX_HEART_RATE_FIELD_NUM, value)

    /** resting_heart_rate. */
    public var restingHeartRate: UByte?
        get() = getFieldValue(RESTING_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(RESTING_HEART_RATE_FIELD_NUM, value)

    /** threshold_heart_rate. */
    public var thresholdHeartRate: UByte?
        get() = getFieldValue(THRESHOLD_HEART_RATE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(THRESHOLD_HEART_RATE_FIELD_NUM, value)

    /** pwr_calc_type. */
    public var pwrCalcType: PwrZoneCalc?
        get() = (getFieldValue(PWR_CALC_TYPE_FIELD_NUM) as? UByte)?.let { PwrZoneCalc.fromValue(it) }
        set(value) = setFieldValue(PWR_CALC_TYPE_FIELD_NUM, value?.value)

    /** functional_threshold_power. */
    public var functionalThresholdPower: UShort?
        get() = getFieldValue(FUNCTIONAL_THRESHOLD_POWER_FIELD_NUM) as? UShort
        set(value) = setFieldValue(FUNCTIONAL_THRESHOLD_POWER_FIELD_NUM, value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val REFERENCE_MESG_FIELD_NUM: UByte = 0u
        public const val REFERENCE_INDEX_FIELD_NUM: UByte = 1u
        public const val TIME_IN_HR_ZONE_FIELD_NUM: UByte = 2u
        public const val TIME_IN_SPEED_ZONE_FIELD_NUM: UByte = 3u
        public const val TIME_IN_CADENCE_ZONE_FIELD_NUM: UByte = 4u
        public const val TIME_IN_POWER_ZONE_FIELD_NUM: UByte = 5u
        public const val HR_ZONE_HIGH_BOUNDARY_FIELD_NUM: UByte = 6u
        public const val SPEED_ZONE_HIGH_BOUNDARY_FIELD_NUM: UByte = 7u
        public const val CADENCE_ZONE_HIGH_BONDARY_FIELD_NUM: UByte = 8u
        public const val POWER_ZONE_HIGH_BOUNDARY_FIELD_NUM: UByte = 9u
        public const val HR_CALC_TYPE_FIELD_NUM: UByte = 10u
        public const val MAX_HEART_RATE_FIELD_NUM: UByte = 11u
        public const val RESTING_HEART_RATE_FIELD_NUM: UByte = 12u
        public const val THRESHOLD_HEART_RATE_FIELD_NUM: UByte = 13u
        public const val PWR_CALC_TYPE_FIELD_NUM: UByte = 14u
        public const val FUNCTIONAL_THRESHOLD_POWER_FIELD_NUM: UByte = 15u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("ReferenceMesg", 0u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[1] = Field("ReferenceIndex", 1u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[2] = Field("TimeInHrZone", 2u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[3] = Field("TimeInSpeedZone", 3u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[4] = Field("TimeInCadenceZone", 4u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[5] = Field("TimeInPowerZone", 5u, BaseType.UINT32, 1000.0, 0.0, "s", false)
            fields[6] = Field("HrZoneHighBoundary", 6u, BaseType.UINT8, 1.0, 0.0, "bpm", false)
            fields[7] = Field("SpeedZoneHighBoundary", 7u, BaseType.UINT16, 1000.0, 0.0, "m/s", false)
            fields[8] = Field("CadenceZoneHighBondary", 8u, BaseType.UINT8, 1.0, 0.0, "rpm", false)
            fields[9] = Field("PowerZoneHighBoundary", 9u, BaseType.UINT16, 1.0, 0.0, "watts", false)
            fields[10] = Field("HrCalcType", 10u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[11] = Field("MaxHeartRate", 11u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[12] = Field("RestingHeartRate", 12u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[13] = Field("ThresholdHeartRate", 13u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[14] = Field("PwrCalcType", 14u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[15] = Field("FunctionalThresholdPower", 15u, BaseType.UINT16, 1.0, 0.0, "", false)
            return fields
        }
    }
}
